import { ref, computed } from 'vue';

/**
 * Cursor-based pagination with a previous-cursor stack.
 */
export function useCursorPagination({ onNavigate } = {}) {
    const cursorStack = ref([]);
    const currentCursor = ref(null);
    const nextCursor = ref(null);
    const previousCursor = ref(null);
    const hasNext = ref(false);

    const hasPrevious = computed(() => cursorStack.value.length > 0 || currentCursor.value !== null);

    /**
     * Call after a successful fetch to ingest the response metadata.
     */
    function applyResponse(response) {
        nextCursor.value = response.next_cursor || null;
        previousCursor.value = response.previous_cursor || null;
        hasNext.value = !!response.has_next;
    }

    /**
     * Navigate to the next page. Saves current cursor onto the stack.
     */
    function goNext() {
        if (!nextCursor.value) return;
        if (currentCursor.value) {
            cursorStack.value.push(currentCursor.value);
        }
        currentCursor.value = nextCursor.value;
        nextCursor.value = null;
        previousCursor.value = null;
        if (onNavigate) onNavigate();
    }

    /**
     * Navigate to the previous page by popping from the cursor stack.
     */
    function goPrevious() {
        const prev = cursorStack.value.pop();
        nextCursor.value = null;
        previousCursor.value = null;
        // If there's nothing on the stack and currentCursor was null, stay on first page
        if (prev === undefined && cursorStack.value.length === 0) {
            currentCursor.value = null;
        } else {
            currentCursor.value = prev !== undefined ? prev : null;
        }
        if (onNavigate) onNavigate();
    }

    /**
     * Reset to first page (no cursor). Called on filter/sort change.
     */
    function reset() {
        cursorStack.value = [];
        currentCursor.value = null;
        nextCursor.value = null;
        previousCursor.value = null;
        hasNext.value = false;
    }

    return {
        cursorStack,
        currentCursor,
        nextCursor,
        previousCursor,
        hasNext,
        hasPrevious,
        goNext,
        goPrevious,
        reset,
        applyResponse,
    };
}
